/**
 * 开通推送。
 *
 * 这张卡只在「还没订阅」时出现，订阅成功后自己消失 —— 常驻的设置项没人会去点，
 * 而提醒是整个 App 能不能起作用的前提（不提醒 = 他不会想起来打开）。
 *
 * ⚠️ iOS 的坑：Web Push 只在已添加到主屏的 PWA 里可用，Safari 标签页里
 * `Notification` 压根不存在。所以顺序必须是「先判断环境 → 再引导」，
 * 而不是直接弹授权框然后失败 —— 失败一次，用户就认定这功能是坏的。
 */
import { useEffect, useState } from 'react'
import { useNotificationPermission, usePush } from '@pwaship/react'
import { pushApi, pushEnvironment } from '../lib/push'

export default function PushCard() {
  const env = pushEnvironment()
  const perm = useNotificationPermission()
  const [key, setKey] = useState<string | null>(null)
  const [devices, setDevices] = useState(0)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const p = usePush({
    vapidPublicKey: key,
    report: async (sub) => { const r = await pushApi.subscribe(sub); if (r.ok) setDevices(r.data.devices) },
    revoke: async (endpoint) => { await pushApi.unsubscribe(endpoint) },
  })

  useEffect(() => {
    void pushApi.key().then((r) => { if (r.ok) { setKey(r.data.key); setDevices(r.data.devices) } })
  }, [])

  if (env === 'unsupported') return null

  // 已开通就收成一行。原本是直接 return null 彻底让位，但那样「这台到底开通没有、
  // 一共开通了几台」在 App 里完全看不到 —— 多设备的时候只能去翻服务器。
  // 一行的成本很低，能自查的价值高得多。
  if (p.subscription && devices > 0) {
    return (
      <div className="rise rise-2" style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px',
        fontSize: 13, fontWeight: 700, color: 'var(--ps-text3)',
      }}>
        <span aria-hidden>🔔</span>
        <span>提醒已开通 · 共 {devices} 台设备</span>
      </div>
    )
  }

  const enable = async () => {
    setBusy(true); setMsg(null)
    try {
      // 授权必须在用户点击的这一下里发起，异步等待之后再调会被浏览器拒掉
      const g = await perm.request()
      if (!g.ok || g.value !== 'granted') { setMsg('没拿到通知权限。到系统设置里打开就能收到了。'); return }
      const s = await p.subscribe()
      setMsg(s.ok ? '开通了，之后有新消费和到点提醒都会推给你。' : '订阅没成功，稍后再试一次。')
    } finally { setBusy(false) }
  }

  return (
    <section className="rise rise-2" style={{
      background: 'var(--ps-accent-soft)', border: '1px solid var(--ps-accent-line)',
      borderRadius: 20, padding: 17, display: 'grid', gap: 11,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ fontSize: 21 }}>🔔</span>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
          {env === 'need-install' ? '先把它加到主屏' : '开通提醒'}
        </h2>
      </div>
      <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: 'var(--ps-text2)' }}>
        {env === 'need-install'
          ? <>iPhone 上只有加到主屏的应用才能收通知。点底部的分享按钮 → 「添加到主屏幕」，再从主屏打开这里。</>
          : <>有新消费（含退款）会推给你，每天到了学习时间也会提醒，没完成会一直催。
              <br />每台设备都要单独开通一次 —— 这台还没开。</>}
      </p>
      {env === 'ready' && (
        <button onClick={enable} disabled={busy} style={{
          minHeight: 50, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: 'var(--ps-accent)', color: 'var(--ps-on-accent)',
          fontSize: 16.5, fontWeight: 800, letterSpacing: '.02em',
        }}>{busy ? '正在开通…' : '开通'}</button>
      )}
      {msg && <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--ps-accent)' }}>{msg}</p>}
    </section>
  )
}
